import { Log, User, UserManager } from 'oidc-client';
import React, { useEffect, useState } from 'react';
import UserManagerService from './UserManagerService';
import { Loader } from '../components';
import GlobalConfig from '../api/GlobalConfig';

const AuthenticationContext = React.createContext<Partial<AuthenticationContextProps>>({});

type AuthenticationContextProps = {
  userManager: UserManager;
  user: User | null;
  isAuthenticated: boolean | null;
  signinRedirect: () => void;
  signinSilent: () => void;
  signinRedirectCallback: () => void;
  silentRefreshCallback: () => void;
  logout: () => void;
};

export type LoginSettingsType = {
  bankIdAuthority: string;
  bankIdClientId: string;
  bankIdUseBiometric: string;
};

type AuthenticationProviderProps = {
  children?: JSX.Element[] | JSX.Element | string | any;
  loginSettings?: LoginSettingsType;
};

export const AuthenticationProvider = (props: AuthenticationProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loginSettings = {
    authority: props.loginSettings?.bankIdAuthority,
    client_id: props.loginSettings?.bankIdClientId,
    scope: 'openid profile ssn cert',
    acr_values: props.loginSettings?.bankIdUseBiometric === 'true' ? 'urn:bankid:bis' : '',
  };
  const useLegacyLogin = GlobalConfig.val().UseLegacyLogin;

  const getUserManager = () => {
    return useLegacyLogin
      ? UserManagerService.userManager
      : UserManagerService.getUserManagerWithSettings(loginSettings);
  };
  const signinRedirectCallback = () => {
    getUserManager()
      .signinRedirectCallback()
      .then((user: Oidc.User) => {
        setUser(user);
        if (isFirstLogin(user)) {
          globalThis.location.href = '/UserProfile';
        } else if (isAcceptanceRequired(user)) {
          globalThis.location.href = '/CommunicationAcceptance';
        } else if (user && user.state) {
          globalThis.location.href = user.state;
        } else {
          globalThis.location.href = '/';
        }
      })
      .catch((error) => {
        console.error('Signin callback error:', error);
        if (!useLegacyLogin) {
          globalThis.location.href = '/signin';
        }
      });
  };

  const isAcceptanceRequired = (user: Oidc.User) => {
    return user?.profile?.acceptance_required?.toLocaleLowerCase() === 'true';
  };

  const isFirstLogin = (user: Oidc.User) => {
    return user?.profile.first_login?.toLocaleLowerCase() === 'true';
  };

  const signinRedirect = () => {
    const currentPath = globalThis.location.pathname;
    const nonReturnPaths = ['/signin', '/case-status', '/case-verify'];
    const returnState = nonReturnPaths.includes(currentPath) ? '/' : currentPath;

    getUserManager().signinRedirect({
      useReplaceToNavigate: true,
      state: returnState,
    });
  };

  const signinSilent = () => {
    getUserManager().signinSilent();
  };

  const silentRefreshCallback = () => {
    getUserManager()
      .signinSilentCallback()
      .then((user) => {
        if (user) {
          setUser(user);
        }
      })
      .catch((err: any) => console.error(err));
  };

  const logout = async () => {
    const userManager = getUserManager();

    if (!useLegacyLogin) {
      try {
        await userManager.removeUser();
        setUser(null);
        setIsAuthenticated(false);
        globalThis.location.href = '/signin';
      } catch (err) {
        console.error('Logout error:', err);
        globalThis.location.href = '/signin';
      }
      return;
    }

    try {
      await userManager.signoutRedirect();
    } catch (err) {
        console.error('Logout error:', err);
      globalThis.location.href = '/';
    }
  };

  const getUser = async () => {
    if (user) {
      return;
    }
    const loggedInUser = await getUserManager().getUser();
    if (loggedInUser) {
      if (!useLegacyLogin && !(await debtorExists())) {
        await getUserManager().removeUser();
        setUser(null);
        setIsAuthenticated(false);
        globalThis.location.href = '/signin?error=ssn_not_found';
        return;
      }
      setUser(loggedInUser);
      setIsAuthenticated(!loggedInUser.expired);
    }

    setIsLoading(false);
  };

  const debtorExists = async (): Promise<boolean> => {
    try {
      const oidcUser = await getUserManager().getUser();
      const token = oidcUser?.id_token;
      if (!token) {
        return true;
      }
      const baseUrl = GlobalConfig.val().GatewayApiUrl;
      const url = `${baseUrl}${baseUrl.endsWith('/') ? '' : '/'}UserProfileModule/HasDebtor`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (response.status !== 200) {
        return true;
      }
      const body = await response.json();
      return body?.data !== false;
    } catch {
      return true;
    }
  };

  const updateAuthenticated = () => {
    if (user) {
      setIsAuthenticated(!user.expired);
    } else {
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    updateAuthenticated();
  }, [user]);

  useEffect(() => {
    if (!isLoading) {
      updateAuthenticated();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    Log.logger = console;
    Log.level = Log.NONE;
    let ignore = false;

    getUserManager().events.addAccessTokenExpired(accessTokenExpired);
    getUserManager().events.addAccessTokenExpiring(accessTokenExpiring);
    getUserManager().events.addSilentRenewError(silentRenewError);
    getUserManager().events.addUserLoaded(userLoaded);
    getUserManager().events.addUserSessionChanged(userSessionChanged);
    getUserManager().events.addUserSignedIn(userSignedIn);
    getUserManager().events.addUserSignedOut(userSignedOut);
    getUserManager().events.addUserUnloaded(userUnloaded);

    if (!ignore) {
      getUser();
    }

    return () => {
      ignore = true;
      getUserManager().events.removeAccessTokenExpired(accessTokenExpired);
      getUserManager().events.removeAccessTokenExpiring(accessTokenExpiring);
      getUserManager().events.removeSilentRenewError(silentRenewError);
      getUserManager().events.removeUserLoaded(userLoaded);
      getUserManager().events.removeUserSessionChanged(userSessionChanged);
      getUserManager().events.removeUserSignedIn(userSignedIn);
      getUserManager().events.removeUserSignedOut(userSignedOut);
      getUserManager().events.removeUserUnloaded(userUnloaded);
    };

    // eslint-disable-next-line
  }, []);

  const userLoaded = (user: User) => {
    setUser(user);
  };

  const userSignedOut = () => {
    setUser(null);
  };

  const userSignedIn = () => {};

  const userUnloaded = () => {
    setUser(null);
  };

  const userSessionChanged = () => {};

  const accessTokenExpired = () => {
    setIsAuthenticated(false);
  };

  const accessTokenExpiring = () => {};

  const silentRenewError = () => {
    setIsAuthenticated(false);
  };

  return (
    <AuthenticationContext.Provider
      value={{
        userManager: getUserManager(),
        user,
        isAuthenticated,
        signinRedirect,
        signinRedirectCallback,
        signinSilent,
        silentRefreshCallback,
        logout,
      }}
    >
      {isLoading || isAuthenticated == null ? (
        <Loader main={true} opacity={1} />
      ) : (
        React.Children.only(props.children)
      )}
    </AuthenticationContext.Provider>
  );
};

const useAuthenticationContext = () => {
  const context = React.useContext(AuthenticationContext);

  if (!context) {
    throw new Error(`There is no context`);
  }

  return context;
};

export const AuthenticationConsumer = AuthenticationContext.Consumer;

export default AuthenticationProvider;

export { useAuthenticationContext };
